import {
  faExclamationTriangle,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams } from '@tanstack/react-router';
import { FC, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from 'src/components/0100_button';
import Input from 'src/components/0100_input';
import ResponseBox from 'src/components/0100_response_box';
import { createModSection } from 'src/graphql/mutations/events.graphql';
import {
  ICreateModSectionMutation,
  ICreateModSectionMutationVariables,
} from 'src/graphql/mutations/events.graphql.types';
import useButtonStates from 'src/hooks/buttonStates/useButtonStates';
import useEventModSections from 'src/hooks/events/useEventModSections';
import { useMutation } from 'urql';
import Section from './Section';

interface IProps {
  canManageMod?: boolean;
  eventAllowsMoreSignup: boolean;
  waitlistAllowed: boolean;
  eventHasEnded: boolean;
  modSignupIsOpen: boolean;
}

const Sections: FC<IProps> = ({
  canManageMod,
  eventAllowsMoreSignup,
  waitlistAllowed,
  eventHasEnded,
  modSignupIsOpen,
}) => {
  const { eventId } = useParams({ strict: false });

  const { buttonState } = useButtonStates();
  const { modSections } = useEventModSections();
  const { register, setValue, watch } = useForm({
    defaultValues: { newSection: '' },
  });
  const [error, setError] = useState<string | null>(null);
  const { newSection } = watch();
  const [createModSectionResult, createModSectionMutation] = useMutation<
    ICreateModSectionMutation,
    ICreateModSectionMutationVariables
  >(createModSection);
  const handleCreateModSection = useCallback(() => {
    createModSectionMutation({
      eventId: Number(eventId),
      name: newSection,
      signupLimit: 5,
    }).then(res => {
      setError(null);
      if (res.data?.createModSection?.event?.id) {
        setValue('newSection', '');
      }

      if (res.data?.createModSection?.error) {
        setError(res.data.createModSection.error);
      }
    });
  }, [createModSectionMutation, newSection, eventId, setValue]);

  if (!canManageMod && Number(modSections?.length) === 0) {
    return (
      <div className="flex justify-center gray-box border-t border-juno-gray-700 py-4 w-full text-juno-gray-200">
        No Mods available yet. Please check back later.
      </div>
    );
  }

  return (
    <div className="w-full grid gap-2">
      {modSections
        ?.sort((a, b) => a.name.localeCompare(b.name))
        .map(x => (
          <Section
            key={x.id}
            canManageMod={canManageMod}
            eventAllowsMoreSignup={eventAllowsMoreSignup}
            waitlistAllowed={waitlistAllowed}
            eventHasEnded={eventHasEnded}
            modSignupIsOpen={modSignupIsOpen}
            {...x}
          />
        ))}
      {canManageMod && (
        <div className="grid gap-2 gray-box py-4 border-t border-juno-gray-700">
          <div className="grid lg:flex justify-center items-center gap-2 grid-cols-1 lg:grid-cols-2 px-4">
            <div>
              <Input
                placeholder="Ex: Saturday Night"
                {...register('newSection')}
              />
            </div>
            <div>
              <Button
                defaultLabel={
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faPlus} />
                    Add Section
                  </div>
                }
                state={buttonState({
                  isHighlight: true,
                  isDirty: true,
                  isFetching: createModSectionResult.fetching,
                  isValid: newSection.trim().length > 0,
                })}
                stateLabel={{ loading: 'Creating Mod Section...' }}
                onClick={handleCreateModSection}
              />
            </div>
          </div>
          {error && (
            <ResponseBox type="error" withIcon={faExclamationTriangle}>
              {error}
            </ResponseBox>
          )}
        </div>
      )}
    </div>
  );
};

export default Sections;
